import React, { useState, useEffect, useContext } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { X, User, Mail, Lock, Eye, EyeOff, ShieldCheck, ClipboardList, Loader2, ShieldAlert } from "lucide-react";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import WhatsappSelect from "../WhatsappSelect";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import useWhatsApps from "../../hooks/useWhatsApps";

const UserSchema = Yup.object().shape({
  name: Yup.string().min(2, "Muy corto!").max(50, "Muy largo!").required("Requerido"),
  password: Yup.string().min(5, "Muy corto!").max(50, "Muy largo!"),
  email: Yup.string().email("Email inválido").required("Requerido"),
});

const UserModal = ({ open, onClose, userId }) => {
  const initialState = { name: "", email: "", password: "", profile: "user", roleId: "" };
  const { user: loggedInUser } = useContext(AuthContext);
  const [user, setUser] = useState(initialState);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [selectedWhatsappIds, setSelectedWhatsappIds] = useState([]);
  const [viewAllWhatsappIds, setViewAllWhatsappIds] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [whatsappId, setWhatsappId] = useState("");
  const [roles, setRoles] = useState([]);
  const { loading: loadingWhatsapps, whatsApps } = useWhatsApps();
  const isSuperAdmin = loggedInUser.profile === "admin";

  useEffect(() => {
    const fetchUser = async () => {
      if (!userId) return;
      try {
        const { data } = await api.get(`/users/${userId}`);
        setUser((prev) => ({ ...prev, ...data, roleId: data.roleId || "" }));
        setSelectedQueueIds(data.queues?.map((q) => q.id) || []);
        setWhatsappId(data.whatsappId || "");
        setSelectedWhatsappIds(data.whatsapps?.map((w) => w.id) || []);
        setViewAllWhatsappIds(
          (data.whatsapps || [])
            .filter((w) => w.UserWhatsapp?.viewAllTickets)
            .map((w) => w.id)
        );
      } catch (err) { toastError(err); }
    };
    fetchUser();
  }, [userId, open]);

  useEffect(() => {
    if (open) {
      api.get("/roles").then(({ data }) => setRoles(data)).catch(() => {});
    }
  }, [open]);

  const handleClose = () => { onClose(); setUser(initialState); setSelectedWhatsappIds([]); setViewAllWhatsappIds([]); };

  const handleSaveUser = async (values) => {
    const userData = {
      ...values,
      whatsappId,
      queueIds: selectedQueueIds,
      whatsappIds: selectedWhatsappIds,
      viewAllWhatsappIds,
    };
    if (userData.roleId === "") userData.roleId = null;
    try {
      if (userId) await api.put(`/users/${userId}`, userData);
      else await api.post("/users", userData);
      toast.success(i18n.t("userModal.success"));
    } catch (err) { toastError(err); }
    handleClose();
  };

  if (!open) return null;

  const FieldWrapper = ({ label, icon: Icon, error, hint, children }) => (
    <div>
      <label className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </label>
      {children}
      {error ? <p className="text-xs text-red-500 mt-1">{error}</p> : hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700 flex-none">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <User className="w-4 h-4 text-indigo-600" />
            {userId ? i18n.t("userModal.title.edit") : i18n.t("userModal.title.add")}
          </h2>
          <button onClick={handleClose} className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <Formik
          initialValues={user}
          enableReinitialize={true}
          validationSchema={UserSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => { handleSaveUser(values); actions.setSubmitting(false); }, 400);
          }}
        >
          {({ touched, errors, isSubmitting }) => (
            <Form className="flex flex-col flex-1 overflow-hidden">
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5 bg-slate-50 dark:bg-slate-800/30">
                {/* Personal info */}
                <div>
                  <p className="flex items-center gap-1.5 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">
                    <User className="w-3.5 h-3.5" /> Información Personal
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <FieldWrapper label={i18n.t("userModal.form.name")} error={touched.name && errors.name}>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Field name="name" autoFocus placeholder="Nombre completo" className={`w-full pl-9 pr-3 py-2.5 text-sm border rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all ${touched.name && errors.name ? "border-red-400" : "border-slate-200 dark:border-slate-700"}`} />
                      </div>
                    </FieldWrapper>
                    <FieldWrapper label={i18n.t("userModal.form.email")} error={touched.email && errors.email}>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Field name="email" type="email" placeholder="correo@ejemplo.com" className={`w-full pl-9 pr-3 py-2.5 text-sm border rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all ${touched.email && errors.email ? "border-red-400" : "border-slate-200 dark:border-slate-700"}`} />
                      </div>
                    </FieldWrapper>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <FieldWrapper label={i18n.t("userModal.form.password")} error={touched.password && errors.password} hint={userId ? "Dejar vacío para mantener" : ""}>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Field name="password" type={showPassword ? "text" : "password"} className={`w-full pl-9 pr-9 py-2.5 text-sm border rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all ${touched.password && errors.password ? "border-red-400" : "border-slate-200 dark:border-slate-700"}`} />
                        <button type="button" onClick={() => setShowPassword((p) => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </FieldWrapper>
                    <Can
                      role={loggedInUser.profile}
                      perform="user-modal:editProfile"
                      permissions={loggedInUser.permissions}
                      yes={() => (
                        <FieldWrapper label="Rol">
                          <div className="relative">
                            <ShieldCheck className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Field as="select" name="roleId" className="w-full pl-9 pr-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all appearance-none">
                              <option value="">Sin rol especifico</option>
                              {roles.map(r => (
                                <option key={r.id} value={r.id}>{r.name}</option>
                              ))}
                            </Field>
                          </div>
                        </FieldWrapper>
                      )}
                    />
                  </div>
                  {isSuperAdmin && (
                    <Field name="profile">
                      {({ field, form }) => (
                        <label className="mt-3 flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={field.value === "admin"}
                            onChange={(e) =>
                              form.setFieldValue("profile", e.target.checked ? "admin" : "user")
                            }
                            className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <ShieldAlert className="w-4 h-4 text-amber-500" />
                          Super Administrador (acceso total, incluye Configuración y Roles)
                        </label>
                      )}
                    </Field>
                  )}
                </div>

                {/* Assignments */}
                <Can
                  role={loggedInUser.profile}
                  perform="user-modal:editQueues"
                  permissions={loggedInUser.permissions}
                  yes={() => (
                    <div>
                      <p className="flex items-center gap-1.5 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">
                        <ClipboardList className="w-3.5 h-3.5" /> Asignaciones
                      </p>
                      <QueueSelect selectedQueueIds={selectedQueueIds} onChange={setSelectedQueueIds} />
                      <div className="mt-3">
                        <WhatsappSelect
                          selectedWhatsappIds={selectedWhatsappIds}
                          onChange={setSelectedWhatsappIds}
                          viewAllWhatsappIds={viewAllWhatsappIds}
                          onViewAllChange={setViewAllWhatsappIds}
                        />
                      </div>
                      {!loadingWhatsapps && (
                        <div className="mt-3">
                          <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">Conexión Predeterminada</label>
                          <select
                            value={whatsappId}
                            onChange={(e) => setWhatsappId(e.target.value)}
                            className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                          >
                            <option value="">Ninguno</option>
                            {whatsApps.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                          </select>
                        </div>
                      )}
                    </div>
                  )}
                />
              </div>
              <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 dark:border-slate-700 flex-none">
                <button type="button" onClick={handleClose} disabled={isSubmitting} className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                  {i18n.t("userModal.buttons.cancel")}
                </button>
                <button type="submit" disabled={isSubmitting} className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors">
                  {isSubmitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  {userId ? i18n.t("userModal.buttons.okEdit") : i18n.t("userModal.buttons.okAdd")}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default UserModal;
